import type { GetPatientDetailsDto } from '@montugroup/pms-api-contracts';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { parseStreetAddress } from './useParseAddress';

export interface PatientDetailsDataState {
  /**
   * basic info
   */
  patient_code: string;
  patient_user_id: number;
  FirstName: string | null;
  LastName: string | null;
  email: string | null;
  phone: string | null;
  street: { line1: string | null; line2: string | null };
  suburb: string | null; //city
  state: number | null; //stateId
  postcode: string | null; //zipCode
  gender: string | null;
  DoB: string | null;
  calculatedAge: number | null;
  isActive: boolean;
  atsi_status: string | null;
  consent: boolean | null;
  discharge_status: string | null;
  pmsDoctor: number | null; // doctor id

  /**
   * care info
   */
  preferedPharmacy?: string | null; // do we still need this?
  HomeDelivery: boolean;
  doctorName: string | null;
  doctorClinic: string | null;
  doctorEmail: string | null;
  doctorPhone: string | null;

  /**
   * identification info
   */
  medicareNumber: string | null;
  medicare_ref_number: number | null;
  medicareExpiration: string | null;
  dvaNumber: string | null;
  ihi_number: string | null;
  ihi_number_status: string | null;
  ihi_record_status: string | null;

  /**
   * red flag info
   */
  redFlag: boolean;
  redFlagComment: string | null;

  /**
   * medical info
   */
  presentingComplaint: string | null; //conditions
  symptoms: string | null;
  trialledMedication: string | null; //medications
  currentMedicationList: string | null;
  cannabisNaive: boolean | null;
  cannabisExperience: string | null; //tried cannabis
  smokingEx: string | null;
  drinksPerWeek: number | null;
  allergies: string | null;
  cardioDisease: string | null;
  medicalHistory: string | null; // comments
  is_allergy: boolean | null;
  is_cardio_disease: boolean | null;
  medical_condition_1: string | null;
  medical_condition_2: string | null;
  medical_condition_3: string | null;
  medical_condition_4: string | null;
  others_1_comment: string | null;
  others_2_comment: string | null;

  /**
   * patient assessment
   */
  detailsConfirmed: boolean;
  medicalSummary: boolean;
  PsychosisHx: boolean;
  psycosis: string | null;
  DrugDrug: boolean;
  DrugDrugComment: string | null;
  SubstanceAbuse: boolean;
  SubstanceAbuseComment: string | null;
  Lactating: boolean;
  LactatingComment: string | null;
  falls: boolean;
  fallsComment: string | null;
  RecentlyIncarcerated: boolean;
  RecentlyIncarceratedComment: string | null;
  SiTosh: boolean;
  SiToshComment: string | null;
  PaymentVerified: boolean;
  treatmentGoal: string | null;
  MCprocess: boolean;
  notes: string | null;
}

export interface PatientAssessmentDto {
  patientDetailsConfirmed: boolean;
  medicalSummaryVerified: boolean;
  psychosis: string | null;
  drugDrug: string | null;
  agePolypharmacy: string | null;
  falls: string | null;
  pregnantLactating: string | null;
  substanceAbuse: string | null;
  recentlyIncarcerated: string | null;
  siTosh: string | null;
  paymentVerified: boolean;
  mcProcessExplained: boolean;
  treatmentGoal: string | null;
  hasPsychosis?: boolean | undefined;
  hasDrugDrug: boolean;
  hasAgePolypharmacy: boolean | null;
  hasFalls: boolean;
  hasPregnantLactating: boolean;
  hasSubstanceAbuse: boolean;
  hasRecentlyIncarcerated: boolean;
  hasSiTosh: boolean;
  notes: string;
}

export const usePatientDetailsData = ({
  patientDetailsData,
  patientAssessmentData,
}: {
  patientDetailsData?: GetPatientDetailsDto;
  patientAssessmentData?: PatientAssessmentDto | null;
}) => {
  const [data, setData] = useState<PatientDetailsDataState>({
    /**
     * basic info
     */
    patient_code: '',
    patient_user_id: 0,
    FirstName: '',
    LastName: '',
    email: '',
    phone: '',
    street: {
      line1: '',
      line2: '',
    },
    suburb: '',
    state: null,
    postcode: '',
    gender: '',
    DoB: '',
    calculatedAge: null,
    isActive: true,
    atsi_status: '',
    consent: null,
    discharge_status: '',
    pmsDoctor: null,
    /**
     * care info
     */
    HomeDelivery: false,
    doctorName: '',
    doctorClinic: '',
    doctorEmail: '',
    doctorPhone: '',

    /**
     * identification info
     */
    medicareNumber: '',
    medicare_ref_number: null,
    medicareExpiration: '',
    dvaNumber: '',
    ihi_number: '',
    ihi_number_status: '',
    ihi_record_status: '',
    /**
     * red flag info
     */
    redFlag: false,
    redFlagComment: '',
    /**
     * medical info
     */
    presentingComplaint: '',
    symptoms: '',
    trialledMedication: '',
    currentMedicationList: '',
    cannabisNaive: null,
    cannabisExperience: '',
    smokingEx: '',
    drinksPerWeek: null,
    allergies: '',
    cardioDisease: '',
    medicalHistory: '',
    is_allergy: null,
    is_cardio_disease: null,
    medical_condition_1: '[]',
    medical_condition_2: '',
    medical_condition_3: '',
    medical_condition_4: '',
    others_1_comment: '',
    others_2_comment: '',
    /**
     * patient assessment
     */
    detailsConfirmed: false,
    medicalSummary: false,
    PsychosisHx: false,
    psycosis: '',
    DrugDrug: false,
    DrugDrugComment: '',
    SubstanceAbuse: false,
    SubstanceAbuseComment: '',
    Lactating: false,
    LactatingComment: '',
    falls: false,
    fallsComment: '',
    RecentlyIncarcerated: false,
    RecentlyIncarceratedComment: '',
    SiTosh: false,
    SiToshComment: '',
    PaymentVerified: false,
    treatmentGoal: '',
    MCprocess: false,
    notes: '',
  });

  const calculateAge = (dob?: string | null) => {
    return dob ? Math.floor(DateTime.now().diff(DateTime.fromISO(dob), ['years']).years) : null;
  };

  useEffect(() => {
    const basicInfo = patientDetailsData?.basicInfo?.attributes;
    const careInfo = patientDetailsData?.careInfo?.attributes;
    const identificationInfo = patientDetailsData?.identificationInfo?.attributes;
    const redFlagInfo = patientDetailsData?.redFlagInfo?.attributes;
    const medicalInfo = patientDetailsData?.medicalInfo?.attributes;
    const address = patientDetailsData?.basicInfo?.attributes?.residentialAddress?.address;
    const contactDetails = patientDetailsData?.contactDetails?.attributes;

    setData((current) => ({
      ...current,
      /**
       * basic info
       */
      patient_code: basicInfo?.patientCode ?? '',
      patient_user_id: basicInfo?.userId ?? 0,
      FirstName: basicInfo?.firstName ?? null,
      LastName: basicInfo?.lastName ?? null,
      email: contactDetails?.email ?? null,
      phone: contactDetails?.phone ?? null,
      street: { ...current?.street, ...parseStreetAddress(address) },
      suburb: basicInfo?.residentialAddress?.city ?? null,
      state: basicInfo?.residentialAddress?.state?.id ?? null,
      postcode: basicInfo?.residentialAddress?.zipCode ?? null,
      gender: basicInfo?.gender ?? null,
      DoB: basicInfo?.dob ?? null,
      calculatedAge: calculateAge(basicInfo?.dob),
      isActive: basicInfo?.userActive ?? false,
      atsi_status: basicInfo?.atsiStatus ?? null,
      consent: basicInfo?.consent ?? null,
      discharge_status: basicInfo?.dischargeStatus ?? null,
      pmsDoctor: basicInfo?.doctorId ?? null,
      /**
       * care info
       */
      HomeDelivery: careInfo?.homeDelivery ?? false,
      doctorName: careInfo?.gpName ?? null,
      doctorClinic: careInfo?.gpClinic ?? null,
      doctorEmail: careInfo?.gpEmail ?? null,
      doctorPhone: careInfo?.gpPhone ?? null,
      /**
       * identification info
       */
      medicareNumber: identificationInfo?.medicareNumber ?? null,
      medicare_ref_number: identificationInfo?.medicareRefNumber ?? null,
      medicareExpiration: identificationInfo?.medicareExpDate ?? null,
      dvaNumber: identificationInfo?.dvaNumber ?? null,
      ihi_number: identificationInfo?.ihiNumber ?? null,
      ihi_number_status: identificationInfo?.ihiNumberStatus ?? null,
      ihi_record_status: identificationInfo?.ihiRecordStatus ?? null,
      /**
       * red flag info
       */
      redFlag: redFlagInfo?.hasRedFlag ?? false,
      redFlagComment: redFlagInfo?.redFlagComment ?? null,
      /**
       * medical info
       */
      presentingComplaint: medicalInfo?.conditions ?? null,
      symptoms: medicalInfo?.symptoms ?? null,
      trialledMedication: medicalInfo?.medications ?? null,
      currentMedicationList: medicalInfo?.currentMedications ?? null,
      cannabisNaive: medicalInfo?.cannabisNaive ?? null,
      cannabisExperience: medicalInfo?.triedCannabis ?? null,
      smokingEx: medicalInfo?.smoking ?? null,
      drinksPerWeek: medicalInfo?.alcoholPerWeek ?? null,
      allergies: medicalInfo?.allergies ?? null,
      cardioDisease: medicalInfo?.cardioDisease ?? null,
      medicalHistory: medicalInfo?.comments ?? null,
      is_allergy: medicalInfo?.hasAllergy ?? null,
      is_cardio_disease: medicalInfo?.hasCardioDisease ?? null,
      medical_condition_1: medicalInfo?.medicalCondition1 ?? '[]',
      medical_condition_2: medicalInfo?.medicalCondition2 ?? '',
      medical_condition_3: medicalInfo?.medicalCondition3 ?? '',
      medical_condition_4: medicalInfo?.medicalCondition4 ?? '',
      others_1_comment: medicalInfo?.othersComment1 ?? '',
      others_2_comment: medicalInfo?.othersComment2 ?? '',

      /** patient assessement */
      detailsConfirmed: patientAssessmentData?.patientDetailsConfirmed ?? false,
      medicalSummary: patientAssessmentData?.medicalSummaryVerified ?? false,
      PsychosisHx: patientAssessmentData?.hasPsychosis ?? false,
      psycosis: patientAssessmentData?.psychosis ?? '',
      DrugDrug: patientAssessmentData?.hasDrugDrug ?? false,
      DrugDrugComment: patientAssessmentData?.drugDrug ?? '',
      SubstanceAbuse: patientAssessmentData?.hasSubstanceAbuse ?? false,
      SubstanceAbuseComment: patientAssessmentData?.substanceAbuse ?? '',
      Lactating: patientAssessmentData?.hasPregnantLactating ?? false,
      LactatingComment: patientAssessmentData?.pregnantLactating ?? '',
      falls: patientAssessmentData?.hasFalls ?? false,
      fallsComment: patientAssessmentData?.falls ?? '',
      RecentlyIncarcerated: patientAssessmentData?.hasRecentlyIncarcerated ?? false,
      RecentlyIncarceratedComment: patientAssessmentData?.recentlyIncarcerated ?? '',
      SiTosh: patientAssessmentData?.hasSiTosh ?? false,
      SiToshComment: patientAssessmentData?.siTosh ?? '',
      PaymentVerified: patientAssessmentData?.paymentVerified ?? false,
      treatmentGoal: patientAssessmentData?.treatmentGoal ?? '',
      MCprocess: patientAssessmentData?.mcProcessExplained ?? false,
      notes: patientAssessmentData?.notes ?? '',
    }));
  }, [patientDetailsData, patientAssessmentData]);

  return {
    data,
    setData,
  };
};
